import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID, Service, signal } from '@angular/core';

export interface CookiePreferences {
  essential: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'etxe_cookie_preferences';

@Service()
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);

  /** Indicates if user has explicitly accepted or declined cookies */
  readonly consentGiven = signal<boolean>(false);

  /** Controls banner visibility */
  readonly showBanner = signal<boolean>(false);

  /** Controls detailed settings modal visibility */
  readonly showCustomizeModal = signal<boolean>(false);

  /** Current cookie preferences state */
  readonly preferences = signal<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  constructor() {
    this.initConsentState();
  }

  /**
   * Initializes consent state from localStorage or defaults
   */
  private initConsentState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<CookiePreferences>;
        const prefs: CookiePreferences = {
          essential: true,
          analytics: Boolean(parsed.analytics),
          marketing: Boolean(parsed.marketing),
        };
        this.preferences.set(prefs);
        this.consentGiven.set(true);
        this.showBanner.set(false);
        this.applyGtagConsent(prefs.analytics, prefs.marketing);
      } else {
        this.consentGiven.set(false);
        this.showBanner.set(true);
        // Default Google Consent Mode v2 to denied until user acts
        this.applyGtagConsent(false, false);
      }
    } catch (err) {
      console.warn('Error reading cookie preferences:', err);
      this.showBanner.set(true);
    }
  }

  /**
   * User accepts all cookies (analytics + marketing)
   */
  public acceptAll(): void {
    this.savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  }

  /**
   * User declines optional cookies (only essential active)
   */
  public declineAll(): void {
    this.savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  }

  /**
   * Save customized cookie preferences
   */
  public savePreferences(prefs: Partial<CookiePreferences>): void {
    const updated: CookiePreferences = {
      essential: true,
      analytics: Boolean(prefs.analytics),
      marketing: Boolean(prefs.marketing),
    };

    this.preferences.set(updated);
    this.consentGiven.set(true);
    this.showBanner.set(false);
    this.showCustomizeModal.set(false);

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {
        console.warn('Unable to save cookie preferences to localStorage', err);
      }
    }

    this.applyGtagConsent(updated.analytics, updated.marketing);
  }

  /**
   * Open cookie preferences settings modal (e.g. from footer link)
   */
  public openPreferences(): void {
    this.showCustomizeModal.set(true);
    this.showBanner.set(true);
  }

  /**
   * Close customize modal view
   */
  public closeCustomizeModal(): void {
    this.showCustomizeModal.set(false);
    if (this.consentGiven()) {
      this.showBanner.set(false);
    }
  }

  /**
   * Send consent update signal to Google Consent Mode v2 (gtag.js)
   */
  private applyGtagConsent(analyticsGranted: boolean, marketingGranted: boolean): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    }

    const consentState = {
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      ad_storage: marketingGranted ? 'granted' : 'denied',
      ad_user_data: marketingGranted ? 'granted' : 'denied',
      ad_personalization: marketingGranted ? 'granted' : 'denied',
    };

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', consentState);
    } else {
      gtag('consent', 'update', consentState);
    }
  }
}
